import {setTitle as setDocumentTitle} from '@github-ui/document-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isMobile} from '@github-ui/get-os'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {XIcon} from '@primer/octicons-react'
import {Flash, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useEffect, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, useFragment} from 'react-relay'

import {ContentWrapper} from '../ContentWrapper'
import type {OptionConfig} from '../OptionConfig'
import type {Header$key} from './__generated__/Header.graphql'
import type {HeaderParentTitle$key} from './__generated__/HeaderParentTitle.graphql'
import type {HeaderQuery} from './__generated__/HeaderQuery.graphql'
import type {IssueMetadata$key} from './__generated__/IssueMetadata.graphql'
import type {TrackedByFragment$key} from './__generated__/TrackedByFragment.graphql'
import styles from './Header.module.css'
import type {IssueSecondaryNode} from './header-secondary-query-types'
import {HeaderEditor} from './HeaderEditor'
import {HeaderMetadata} from './HeaderMetadata'
import {HeaderViewer} from './HeaderViewer'
import {JumpToBottom} from './JumpToBottom'
import {useTitleMirror} from './use-title-mirror'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const HeaderGraphqlQuery = graphql`
  query HeaderQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...Header
      }
    }
  }
`

export type HeaderSecondaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'isTransferInProgress'>>

type HeaderBaseProps = {
  optionConfig: OptionConfig
  issueSecondary?: HeaderParentTitle$key | TrackedByFragment$key | IssueMetadata$key
  headerSecondary?: HeaderSecondaryFragment
}

export type HeaderFetchedProps = HeaderBaseProps & {
  issuesQueryRef: PreloadedQuery<HeaderQuery>
}

type HeaderProps = HeaderBaseProps & {
  issue: Header$key
}

export function Header({issue, issueSecondary, headerSecondary, optionConfig}: HeaderProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment Header on Issue {
        title
        number
        id
        repository {
          nameWithOwner
          id
        }
        ...HeaderViewer
        ...HeaderMetadata
      }
    `,
    issue,
  )
  // If the secondary data is not available, we default to false
  const isTransferInProgress = headerSecondary?.isTransferInProgress ?? false

  const {
    id,
    title: issueTitle,
    number,
    repository: {nameWithOwner},
  } = data

  const titleRef = useRef<HTMLInputElement>(null)
  const [isIssueTitleEditActive, setIsIssueTitleEditActive] = useState(false)

  // Local title that mirrors the server-provided `issueTitle` when not editing.
  // Updates synchronously in the same render so downstream consumers see the
  // synced value without an extra commit.
  const [title, setTitle] = useTitleMirror(issueTitle, isIssueTitleEditActive)

  const shouldUpdateDocumentTitle =
    optionConfig.selectedProjectId === undefined && !optionConfig.shouldSkipSetDocumentTitle
  useEffect(() => {
    if (issueTitle && shouldUpdateDocumentTitle) {
      setDocumentTitle(`${title} · Issue #${number} · ${nameWithOwner}`)
    }
  }, [shouldUpdateDocumentTitle, issueTitle, nameWithOwner, number, title])

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldValue = title
    const newValue = e.target.value
    if (newValue !== null && newValue !== oldValue) {
      setTitle(newValue)
    }
  }

  // Focus on title when user clicks on edit
  useEffect(() => {
    if (isIssueTitleEditActive && titleRef.current) {
      titleRef.current.focus()
    }
  }, [isIssueTitleEditActive])

  const {isSticky, hasRoots, sensorRef} = useStickyHeader()
  const [transferWarningDismissed, setTransferWarningDismissed] = useState(false)
  const showTransferWarning = isTransferInProgress && !transferWarningDismissed

  return (
    <>
      <ContentWrapper>
        {showTransferWarning && (
          <Flash aria-live="polite" className={styles.transferWarningFlash}>
            Issue transfer in progress
            <IconButton
              variant="invisible"
              aria-label="Dismiss issue is in transfer notification"
              icon={XIcon}
              onClick={() => setTransferWarningDismissed(true)}
            />
          </Flash>
        )}
        {isIssueTitleEditActive ? (
          <HeaderEditor
            issueId={id}
            title={title}
            titleRef={titleRef}
            onTitleChange={onTitleChange}
            onIssueUpdate={optionConfig.onIssueUpdate}
            cancelIssueTitleEdit={() => {
              setIsIssueTitleEditActive(false)
            }}
            isDirty={issueTitle !== title}
          />
        ) : (
          <HeaderViewer
            headerViewerKey={data}
            optionConfig={optionConfig}
            setIsIssueTitleEditActive={setIsIssueTitleEditActive}
            parentKey={issueSecondary as HeaderParentTitle$key}
            issueTitle={title}
          />
        )}
      </ContentWrapper>
      <HeaderMetadata
        headerMetadataKey={data}
        optionConfig={optionConfig}
        isSticky={hasRoots && isSticky}
        setIsIssueTitleEditActive={setIsIssueTitleEditActive}
        headerMetadataSecondaryKey={issueSecondary}
      />
      {isMobile() && <JumpToBottom />}
      <div ref={sensorRef} className={styles.stickyObservableBox} />
      <div
        className={clsx(
          styles.HeaderContentWrapper,
          isFeatureEnabled('issues_sticky_header') && styles.HeaderContentWrapper_stickyHeader,
          isFeatureEnabled('issue_inline_avatars') && styles.HeaderContentWrapper_inlineAvatars,
        )}
      >
        <ContentWrapper>
          <div className={styles.headerSpacer} />
        </ContentWrapper>
      </div>
    </>
  )
}
